/**
 * Viewport-pinned overlay. Must portal to document.body.
 * A `position:fixed` child of `[data-cmc-scroll=workplane]` (framer-motion
 * transform) is fixed to that ancestor, not the viewport — Team rules
 * scrolled out of view. Do not mount sheets inside the workplane.
 *
 * Wave 0: sticky header/footer, body scroll reset on open, initial focus,
 * Escape + focus return via useEsc / useOverlayLock.
 *
 * dock="right" | "left" | "center" (default)
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { html, M, SOFT, useEsc, useOverlayLock } from "./ui.js";

export function ViewportSheet({
  title,
  wide,
  dock,
  onClose,
  children,
  footer,
  initialFocus = "close",
}) {
  useEsc(onClose);
  useOverlayLock(true);
  const closeRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = 0;
    const prefer = initialFocus === "body"
      ? (body && body.querySelector("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"))
      : closeRef.current;
    const t = requestAnimationFrame(() => {
      if (prefer && typeof prefer.focus === "function") {
        prefer.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(t);
  }, [title, initialFocus]);

  if (typeof document === "undefined" || !document.body) return null;
  const side = dock === "right" || dock === "left" ? dock : "center";
  return createPortal(html`<div
      className=${"ai-studio__sheet ai-studio__sheet--" + side + (wide ? " ai-studio__sheet--wide" : "")}
      data-cmc-viewport-sheet="1"
      data-dock=${side}
      role="dialog" aria-modal="true" aria-label=${title}>
    <button type="button" className="ai-studio__scrim" onClick=${onClose} aria-label="Close overlay"/>
    <${M.div} className=${"ai-studio__panel" + (wide ? " ai-studio__panel--wide" : "") + (side !== "center" ? " ai-studio__panel--dock" : "")}
      initial=${side === "right"
        ? { opacity: 0, x: 28 }
        : side === "left"
          ? { opacity: 0, x: -28 }
          : { opacity: 0, y: 16 }}
      animate=${{ opacity: 1, x: 0, y: 0 }}
      exit=${side === "right"
        ? { opacity: 0, x: 16 }
        : side === "left"
          ? { opacity: 0, x: -16 }
          : { opacity: 0, y: 10 }}
      transition=${SOFT}>
      <header className="ai-studio__panel-hd">
        <h3>${title}</h3>
        <button type="button" className="btn btn--ghost btn--sm" ref=${closeRef}
          onClick=${onClose}>Close</button>
      </header>
      <div className="ai-studio__panel-body" ref=${bodyRef} data-cmc-sheet-body="1">${children}</div>
      ${footer ? html`<footer className="ai-studio__panel-ft">${footer}</footer>` : null}
    <//>
  </div>`, document.body);
}

export default ViewportSheet;
